import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import get from 'lodash/get';
import map from 'lodash/map';
import noop from 'lodash/noop';

import { useSnackBar } from '@sbv-clients/ui-shared';
import { UnwrapArray } from '@sbv-clients/util-misc';

import {
  Api,
  PositionApiPositionGetRequest,
  SchedulerPositionCacheKeys,
  UpdatePositionDto,
  useApi,
} from '../index';

export const usePositionsGet = (
  positionApiPositionGetRequest?: PositionApiPositionGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [
      SchedulerPositionCacheKeys.Positions,
      positionApiPositionGetRequest,
    ],
    queryFn: () =>
      api.positionApi
        .positionGet(positionApiPositionGetRequest)
        .then((response) => response?.data?.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};
export type PositionResponseType = NonNullable<
  UnwrapArray<ReturnType<typeof usePositionsGet>['data']>
>;

/**
 * Fetches a single position by ID (includes positionCoverages).
 * Use when opening edit modal so saved coverages display correctly.
 */
export const usePositionGetById = (positionId: number | undefined) => {
  const api = useApi();
  return useQuery({
    queryKey: [SchedulerPositionCacheKeys.Positions, positionId],
    queryFn: () =>
      api.positionApi
        .positionIdGet({ id: positionId! })
        .then((response) => response?.data?.data),
    enabled: positionId !== undefined && positionId !== null,
    retry: false,
  });
};

export const usePositionOptionsGet = (
  positionApiPositionGetRequest?: PositionApiPositionGetRequest
) => {
  const {
    data: positions = [],
    isLoading,
    isSuccess,
  } = usePositionsGet(positionApiPositionGetRequest);
  const options = useMemo(
    () =>
      map(positions, (item) => ({
        id: String(item.positionId),
        label: item.positionName ?? '',
        value: item.positionId,
      })),
    [positions]
  );
  return { options, isLoading, isSuccess };
};

const updatePosition =
  (api: Api) => (body: UpdatePositionDto & { positionId: number }) => {
    const { positionId, ...rest } = body;
    const updatePositionDto: UpdatePositionDto = {
      ...rest,
      positionCoverages: rest.positionCoverages ?? [],
    };
    return api.positionApi.positionIdPut({
      id: positionId,
      updatePositionDto,
    });
  };
export const useUpdatePosition = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError } = useSnackBar();

  return useMutation(updatePosition(api), {
    onSuccess: async () => {
      queryClient
        .invalidateQueries([SchedulerPositionCacheKeys.Positions])
        .catch(noop);
    },
    onError: async (error) => {
      const message =
        get(error, 'response.data.message', '') || get(error, 'message', '');
      notifyError(message);
    },
  });
};

export const useSyncAllowableBudgetGroups = () => {
  const api = useApi();
  return useMutation(() =>
    api.positionApi.positionSyncAllowableBudgetGroupsPost()
  );
};
